/**
 * A Highcharts plugin for exporting data from a rendered chart as CSV, XLS or HTML table
 *
 * Author:   Torstein Honsi
 * Licence:  MIT
 * Version:  1.3.6
 */
/*global Highcharts, window, document, Blob */
(function (factory) {
    if (typeof module === 'object' && module.exports) {
        module.exports = factory;
    } else {
        factory(Highcharts);
    }
}(function (Highcharts) {
    'use strict';

    var each = Highcharts.each,
        downloadAttrSupported = document.createElement('a').download !== undefined;

    Highcharts.setOptions({
        lang: {
            downloadCSV: 'Download CSV',
            downloadXLS: 'Download XLS'
        }
    });


    /**
     * Get the data rows as a two dimensional array
     */
    Highcharts.Chart.prototype.getDataRows = function () {
        var options = (this.options.exporting || {}).csv || {},
            xAxis = this.xAxis[0],
            rows = {},
            rowArr = [],
            dataRows,
            names = [],
            i,
            x,
            // Pie charts and heat maps store their data differently from line charts
            thisIsALineOrBarChart = !this.series[0].valueData && !this.series[0].center,
            thisIsAHeatMap = this.series[0].valueData,
            thisIsAPieChart = this.series[0].center,

            // Options
            dateFormat = options.dateFormat || '%Y-%m-%d %H:%M:%S',
            columnHeaderFormatter = options.columnHeaderFormatter || function (series, key, keyLength) {
                    return series.name + (keyLength > 1 ? ' ('+ key + ')' : '');
                };

        if (thisIsALineOrBarChart) {
            // Loop the series and index values
            var visibleRange = xAxis.getExtremes();
            i = 0;
            each(this.series, function (series) {
                var requireSorting = series.requireSorting;

                if (series.options.includeInCSVExport !== false && series.visible !== false) { // #55
                    names.push(columnHeaderFormatter(series, 'y', 1));

                    each(series.xData, function (xValue, pIdx) {
                        var yValue = series.yData[pIdx];
                        // filter null values and data outside the visible range
                        if (yValue === null || xValue < visibleRange.min || xValue > visibleRange.max) return;

                        var key = requireSorting ? xValue : pIdx;

                        if (!rows[key]) {
                            rows[key] = [];
                        }
                        rows[key].x = xValue;
                        rows[key][i] = yValue;

                    });
                    i += 1;
                }
            });

            // Make a sortable array
            for (x in rows) {
                if (rows.hasOwnProperty(x)) {
                    rowArr.push(rows[x]);
                }
            }
            // Sort it by X values
            rowArr.sort(function (a, b) {
                return a.x - b.x;
            });

            // Add header row
            dataRows = [[xAxis.isDatetimeAxis ? 'DateTime' : 'Category'].concat(names)];

            // Transform the rows to CSV
            each(rowArr, function (row) {

                var category = row.name;
                if (!category) {
                    if (xAxis.isDatetimeAxis) {
                        category = Highcharts.dateFormat(dateFormat, row.x);
                    } else if (xAxis.categories) {
                        category = Highcharts.pick(xAxis.names[row.x], xAxis.categories[row.x], row.x)
                    } else {
                        category = row.x;
                    }
                }

                // Add the X/date/category
                row.unshift(category);
                dataRows.push(row);
            });

        } else if (thisIsAHeatMap) {

            var datapoints = this.series[0].valueData,
                dateTimes = this.series[0].processedXData,
                lastDateTime,
                dataRows = [],
                currentRow = [],
                previousRow = [],
                startANewRow;

            // Loop through once for each date/time
            // Create a row to hold data for each date/time
            // When there are duplicate date/time values,
            // store all data points with that date/time in the same row
            for (var i = 0; i < datapoints.length; i++) {
                lastDateTime = dateTimes[i-1];
                startANewRow = (dateTimes[i] !== lastDateTime);

                if (startANewRow) {
                    // Save current row's data since we're starting a new one
                    if (currentRow.length > 0) dataRows.push(currentRow);
                    previousRow = currentRow;
                    // Add a new date/time and a data point to a row
                    currentRow = [Highcharts.dateFormat(dateFormat, dateTimes[i]), datapoints[i]];
                } else {
                    currentRow.push(datapoints[i]);
                    // If this is the last row, make sure to save it before quitting
                    if (!dateTimes[i+1]) dataRows.push(currentRow);
                }
            }

            // Gather category names for each column
            each(this.series[0].yAxis.categories, function (category) {
              names.push(category);
            });

            // Add header row
            dataRows.unshift(['Date/Time'].concat(names));

        } else if (thisIsAPieChart) {
            dataRows = [];
            // Add rows for each set of data (array of strings and numbers)
            each(this.series[0].data, function (obj) {
                dataRows.push([obj.name + ' - ' + obj.category, obj.percentage]);
            });
        }

        return dataRows;
    };

    /**
     * Get a CSV string
     */
    Highcharts.Chart.prototype.getCSV = function (useLocalDecimalPoint) {
        var csv = '',
            rows = this.getDataRows(),
            options = (this.options.exporting || {}).csv || {},
            itemDelimiter = options.itemDelimiter || ',', // use ';' for direct import to Excel
            lineDelimiter = options.lineDelimiter || '\n'; // '\n' isn't working with the js csv data extraction

        // Get timezone from options
        const timezone = this.time.options.timezone;

        // Transform the rows to CSV
        each(rows, function (row, i) {
            var val = '',
                j = row.length,
                n = useLocalDecimalPoint ? (1.1).toLocaleString()[1] : '.';
            while (j--) {
                val = row[j];
                // Check if val is undefined or null
                if(val == null) val = '';

                // Convert date/time values to desired format and timezone
                if (typeof val === "string" && moment(val, "YYYY-MM-DD HH:mm:ss", true).isValid()) {
                    val = moment(val, "YYYY-MM-DD HH:mm:ss").tz(timezone).format('YYYY-MM-DD HH:mm:ss');
                }

                if (typeof val === "string") {
                    val = '"' + val + '"';
                }
                if (typeof val === 'number') {
                    if (n === ',') {
                        val = val.toString().replace(".", ",");
                    }
                }
                row[j] = val;
            }
            // Add the values
            csv += row.join(itemDelimiter);

            // Add the line delimiter
            if (i < rows.length - 1) {
                csv += lineDelimiter;
            }
        });
        return csv;
    };

    /**
     * Build a HTML table with the data
     */
    Highcharts.Chart.prototype.getTable = function (useLocalDecimalPoint) {
        var html = '<table>',
            rows = this.getDataRows();

        // Transform the rows to HTML
        each(rows, function (row, i) {
            var tag = i ? 'td' : 'th',
                val,
                j,
                n = useLocalDecimalPoint ? (1.1).toLocaleString()[1] : '.';

            html += '<tr>';
            for (j = 0; j < row.length; j = j + 1) {
                val = row[j];

                // Check if val is undefined or null
                if(val == null) val = '';

                // Add the cell
                if (typeof val === 'number') {
                    if (n === ',') {
                        html += '<' + tag + ' class="number">' + val.toString().replace(".", ",") + '</' + tag + '>';
                    } else {
                        html += '<' + tag + ' class="number">' + val.toString() + '</' + tag + '>';
                    }
                } else {
                    html += '<' + tag + '>' + val + '</' + tag + '>';
                }
            }

            html += '</tr>';
        });
        html += '</table>';
        return html;
    };

    function getContent(chart, href, extension, content, MIME) {
        var a,
            blobObject,
            name,
            options = (chart.options.exporting || {}).csv || {},
            url = options.url || 'http://www.highcharts.com/studies/csv-export/download.php';

        if (chart.options.exporting.filename) {
            name = chart.options.exporting.filename;
        } else if (chart.title) {
            name = chart.title.textStr.replace(/ /g, '-').toLowerCase();
        } else {
            name = 'chart';
        }

        // Download attribute supported
        if (downloadAttrSupported) {
            a = document.createElement('a');
            a.href = window.URL.createObjectURL ? window.URL.createObjectURL(new Blob([content])) : href;
            a.target      = '_blank';
            a.download    = name + '.' + extension;
            document.body.appendChild(a);
            a.click();
            a.remove();

        } else if (window.Blob && window.navigator.msSaveOrOpenBlob) {
            // Falls to msSaveOrOpenBlob if download attribute is not supported
            blobObject = new Blob([content]);
            window.navigator.msSaveOrOpenBlob(blobObject, name + '.' + extension);

        } else {
            // Fall back to server side handling
            Highcharts.post(url, {
                data: content,
                type: MIME,
                extension: extension
            });
        }
    }

    /**
     * Call this on click of 'Download CSV' button
     */
    Highcharts.Chart.prototype.downloadCSV = function () {
        var csv = this.getCSV(true);
        getContent(
            this,
            'data:text/csv,' + csv.replace(/\n/g, '%0A'),
            'csv',
            csv,
            'text/csv'
        );
    };

    /**
     * Call this on click of 'Download XLS' button
     */
    Highcharts.Chart.prototype.downloadXLS = function () {
        var uri = 'data:application/vnd.ms-excel;base64,',
            template = '<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">' +
                '<head><!--[if gte mso 9]><xml><x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet>' +
                '<x:Name>Ark1</x:Name>' +
                '<x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions></x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]-->' +
                '<style>td{border:none;font-family: Calibri, sans-serif;} .number{mso-number-format:"0.00";}</style>' +
                '<meta name=ProgId content=Excel.Sheet>' +
                '</head><body>' +
                this.getTable(true) +
                '</body></html>',
            base64 = function (s) {
                return window.btoa(unescape(encodeURIComponent(s))); // #50
            };
        getContent(
            this,
            uri + base64(template),
            'xls',
            template,
            'application/vnd.ms-excel'
        );
    };


    // Add "Download CSV" to the exporting menu. Use download attribute if supported, else
    // run a simple PHP script that returns a file. The source code for the PHP script can be viewed at
    // https://raw.github.com/highslide-software/highcharts.com/master/studies/csv-export/csv.php
    if (Highcharts.getOptions().exporting) {
        Highcharts.getOptions().exporting.buttons.contextButton.menuItems.push({
            textKey: 'downloadCSV',
            onclick: function () { this.downloadCSV(); }
        }, {
            textKey: 'downloadXLS',
            onclick: function () { this.downloadXLS(); }
        });
    }

}));


//////////////////
// WEBPACK FOOTER
// ./srv/public/js/lib_modified/highstock-exporting/export-csv.js
// module id = 1050
// module chunks = 8